import { db } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Banner } from '@/domain/types/collections';
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';

/**
 * LÓGICA DE NEGOCIO PARA GESTIÓN DE BANNERS
 *
 * REGLAS:
 * - Máximo 3 banners activos simultáneamente
 * - Al activar un 4to banner, se desactiva automáticamente el más antiguo
 * - Los banners activos se ordenan por fecha de creación (más reciente primero)
 * - El campo 'order' (1-3) indica la prioridad visual
 */

/**
 * Obtiene todos los banners activos ordenados por fecha de creación
 * @returns Promise con array de banners activos
 */
export const getActiveBanners = async (): Promise<Banner[]> => {
  try {
    const bannersRef = collection(db, DOCUMENTS_DB.BANNERS);
    const q = query(
      bannersRef,
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
    );

    const querySnapshot = await getDocs(q);
    const banners: Banner[] = [];

    querySnapshot.forEach((doc) => {
      banners.push({ id: doc.id, ...doc.data() } as Banner);
    });

    return banners;
  } catch (error) {
    console.error('Error fetching active banners:', error);
    return [];
  }
};

/**
 * Desactiva el banner activo más antiguo
 * Se ejecuta automáticamente al intentar activar un 4to banner
 * @returns Promise con el ID del banner desactivado o null
 */
export const deactivateOldestBanner = async (): Promise<string | null> => {
  try {
    const activeBanners = await getActiveBanners();

    if (activeBanners.length === 0) {
      return null;
    }

    // El último del array es el más antiguo (ya están ordenados desc)
    const oldestBanner = activeBanners[activeBanners.length - 1];

    if (!oldestBanner.id) {
      return null;
    }

    // Desactivar el banner más antiguo
    const bannerRef = doc(db, DOCUMENTS_DB.BANNERS, oldestBanner.id);
    await updateDoc(bannerRef, {
      isActive: false,
      order: 0,
    });

    return oldestBanner.id;
  } catch (error) {
    console.error('Error deactivating oldest banner:', error);
    return null;
  }
};

/**
 * Verifica si se debe desactivar un banner al activar uno nuevo
 * @returns Promise con booleano indicando si se desactivó alguno
 */
export const checkAndDeactivateIfNeeded = async (): Promise<{
  deactivated: boolean;
  deactivatedId?: string;
}> => {
  try {
    const activeBanners = await getActiveBanners();

    // Si ya hay 3 o más activos, desactivar el más antiguo
    if (activeBanners.length >= 3) {
      const deactivatedId = await deactivateOldestBanner();
      return {
        deactivated: true,
        deactivatedId: deactivatedId || undefined,
      };
    }

    return { deactivated: false };
  } catch (error) {
    console.error('Error checking active banners:', error);
    return { deactivated: false };
  }
};

/**
 * Reordena los banners activos asignando orden 1, 2, 3
 * según su fecha de creación (más reciente = orden 1)
 * @returns Promise void
 */
export const reorderActiveBanners = async (): Promise<void> => {
  try {
    const activeBanners = await getActiveBanners();

    // Actualizar el orden de cada banner
    const updatePromises = activeBanners.map((banner, index) => {
      if (!banner.id) return Promise.resolve();

      const bannerRef = doc(db, DOCUMENTS_DB.BANNERS, banner.id);
      return updateDoc(bannerRef, {
        order: index + 1, // 1, 2, 3
      });
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error reordering banners:', error);
  }
};

/**
 * Obtiene el número de banners activos
 * @returns Promise con el conteo
 */
export const getActiveBannersCount = async (): Promise<number> => {
  const activeBanners = await getActiveBanners();
  return activeBanners.length;
};
